import { Suspense, lazy, type ComponentType, type ReactNode } from 'react';
import { Link, Route } from 'react-router-dom';
import { screens, webScreens, webPath, DOC_BOARDS, type Screen } from '@screens';
import ScreenPage from './ScreenPage';
import { DesignBoardPage, DesignSwitch } from './dc/DesignSwitch';

const CanvasHome = lazy(() => import('./CanvasHome'));
const CatalogPage = lazy(() => import('./CatalogPage'));
// --- slice: tasks (task dispatch engine) ---
const TaskDispatchBoard = lazy(() => import('./pages/TaskDispatchBoard'));
// slice: scan
const ItemBinLookupPage = lazy(() => import('../scan/ItemBinLookupPage'));
// --- slice: intake (cruise PO intake) ---
const CruiseOrderImports = lazy(() => import('./pages/CruiseOrderImports'));
const CruiseOrderImportReview = lazy(() => import('./pages/CruiseOrderImportReview'));
// --- slice: dispatch (pallet -> stage -> load -> depart -> POD) ---
const PalletBuildPage = lazy(() => import('./pages/dispatch/PalletBuildPage'));
const DispatchStagingPage = lazy(() => import('./pages/dispatch/StagingPage'));
const DispatchLoadPage = lazy(() => import('./pages/dispatch/LoadPage'));
const DispatchGatePage = lazy(() => import('./pages/dispatch/GatePage'));
const DispatchPodReviewPage = lazy(() => import('./pages/dispatch/PodReviewPage'));
// --- end slice: dispatch ---
// --- slice: picking (wave planning + work monitor over /waves) ---
const WavePlanningPage = lazy(() => import('./pages/picking/WavePlanningPage'));
const WorkMonitorPage = lazy(() => import('./pages/picking/WorkMonitorPage'));
// --- end slice: picking ---

/** Boards with a purpose-built page instead of the generated shell. */
const CUSTOM_PAGES: Record<string, ComponentType> = {
  Home: CanvasHome,
  Catalog: CatalogPage,
  // --- slice: tasks: SCR-093 supervisor task dispatch board reading /tasks ---
  SCR093: TaskDispatchBoard,
  // slice: scan — SCR-258
  ItemBinLookup: ItemBinLookupPage,
  // --- slice: intake: SCR-243 upload + import list, SCR-252 review/override/commit/allocation (/order-imports) ---
  CruiseOrders: CruiseOrderImports,
  CruiseOrderRecord: CruiseOrderImportReview,
  // --- slice: dispatch: pallet build, staging, load, gate out, POD review over /dispatch ---
  SCR119: PalletBuildPage,
  SCR117: DispatchStagingPage,
  SCR120: DispatchLoadPage,
  SCR115: DispatchGatePage,
  SCR221: DispatchPodReviewPage,
  // --- end slice: dispatch ---
  // --- labels slice (P0-LABEL-001) ---
  LblPrintStation: lazy(() => import('../pages/Labels/LabelPrintStationPage')),
  LblHistory: lazy(() => import('../pages/Labels/LabelHistoryPage')),
  // --- end labels slice ---
  // --- slice: picking: SCR-090 (rendered at its wiredWeb /waves by App.tsx) and SCR-092 ---
  WavePlanning: WavePlanningPage,
  WorkMonitor: WorkMonitorPage,
  // --- end slice: picking ---
  // --- slice: inbound-floor: receiving station (receive, QC, discrepancy), putaway, cycle counts ---
  Receiving: lazy(() => import('./pages/inbound/ReceivingWorkbenchPage')),
  SCR047: lazy(() => import('./pages/inbound/ReceiptByPoPage')),
  SCR048: lazy(() => import('./pages/inbound/ReceivingDiscrepancyPage')),
  SCR052: lazy(() => import('./pages/inbound/InboundQcPage')),
  SCR071: lazy(() => import('./pages/inbound/PutawayQueuePage')),
  SCR066: lazy(() => import('./pages/inbound/CycleCountPlanPage')),
  SCR067: lazy(() => import('./pages/inbound/CountVarianceReviewPage')),
  // --- end slice: inbound-floor ---
};

/** Boards whose canvas route renders a purpose-built working page (1:1 design via ?view=design). */
export const customPageBoards: ReadonlySet<string> = new Set(Object.keys(CUSTOM_PAGES));

/**
 * Boards a working page at `pathname` implements: purpose-built canvas pages and wiredWeb routes.
 * Those pages stay the default and offer "View 1:1 design" (?view=design).
 */
export function workingPageBoards(pathname: string): string[] {
  return screens
    .filter((s) => s.device !== 'phone' && (s.wiredWeb === pathname || (customPageBoards.has(s.board) && webPath(s) === pathname)))
    .map((s) => s.board);
}

/** Wrap a working page so its boards can be previewed 1:1 (?view=design). */
export function withDesignSwitch(pathname: string, page: ReactNode) {
  return <DesignSwitch boards={workingPageBoards(pathname)}>{page}</DesignSwitch>;
}

// --- slice: dispatch: station mode renders the same real pages ---
/** Purpose-built page for a board, if any (used by station mode). */
export function customPageFor(board: string): ComponentType | undefined {
  return CUSTOM_PAGES[board];
}
// --- end slice: dispatch ---

const isFullscreen = (s: Screen) => s.device === 'tv';

function routePath(s: Screen): string {
  return webPath(s).replace(/^\//, '');
}

/** Web screens plus the platform documentation boards (the catalog links to them too). */
const routedScreens: Screen[] = [...webScreens, ...screens.filter((s) => DOC_BOARDS.has(s.board) && s.device !== 'phone')];

/**
 * One route per canvas web screen, mounted inside DashboardLayout. Screens without a working page
 * render their artboard 1:1 (ScreenPage -> BoardView). Screens that already have an implemented
 * page keep that page at its existing route (App.tsx) or as a CUSTOM_PAGES entry, with a
 * "View 1:1 design" link (?view=design, added by ScreenAccessGate). TV wallboards are excluded:
 * see canvasFullscreenRoutes(). /design/:board renders any compiled board (phone boards too).
 */
export function canvasRoutes() {
  const seen = new Set<string>();
  return routedScreens
    .filter((s) => {
      const p = webPath(s);
      if (isFullscreen(s) || s.wiredWeb === p || seen.has(p)) return false;
      seen.add(p);
      return true;
    })
    .map((s) => {
      const Custom = CUSTOM_PAGES[s.board];
      const element = Custom ? (
        <Suspense fallback={<div className="cv" style={{ padding: 28 }} aria-busy="true">Loading…</div>}>
          <Custom />
        </Suspense>
      ) : (
        <ScreenPage screen={s} />
      );
      return <Route key={s.board} path={routePath(s)} element={element} />;
    })
    .concat(<Route key="__design" path="design/:board" element={<DesignBoardPage />} />);
}

/** Full-screen frame for a TV wallboard: no sidebar or header, one way back to the app. */
function Wallboard({ screen }: { screen: Screen }) {
  return (
    <div className="cv" style={{ minHeight: '100vh', background: 'var(--cv-bg)' }}>
      <Link to="/home" className="cv-btn cv-btn-s tv-exit">
        Exit wallboard
      </Link>
      <main id="main-content">
        <ScreenPage screen={screen} />
      </main>
    </div>
  );
}

/**
 * TV wallboards (device 'tv', e.g. boards 'Wallboard' and 'SalesWallboard'), rendered full screen.
 * Mount these OUTSIDE DashboardLayout (paths are absolute, e.g. "/s/wallboard").
 */
export function canvasFullscreenRoutes() {
  const seen = new Set<string>();
  return screens
    .filter((s) => {
      const p = webPath(s);
      if (!isFullscreen(s) || seen.has(p)) return false;
      seen.add(p);
      return true;
    })
    .map((s) => <Route key={s.board} path={webPath(s)} element={<Wallboard screen={s} />} />);
}
